import { InjectionToken, Provider } from '@angular/core';

/**
 * UI-agnostic confirmation contract used by shared editor surfaces before
 * executing destructive store operations.
 *
 * Wrapping confirmation behind DI keeps shared components independent from any
 * specific modal library while preserving the current synchronous confirm flow.
 */
export interface ConfirmationService {
	/**
	 * Asks the user to confirm a destructive action.
	 *
	 * @param message - User-facing confirmation prompt.
	 * @returns `true` when the action should continue.
	 */
	confirm(message: string): boolean;
}

/**
 * Injection token exposing the current confirmation implementation.
 */
export const CONFIRMATION_SERVICE = new InjectionToken<ConfirmationService>(
	'CONFIRMATION_SERVICE'
);

/**
 * Registers the default browser-backed confirmation implementation.
 *
 * The platform currently relies on `window.confirm` for destructive slot-tree
 * actions. Keeping that as the default avoids changing UX while allowing tests
 * and future hosts to override the behaviour through DI.
 *
 * @returns Angular provider for the default confirmation implementation.
 */
export function provideDefaultConfirmation(): Provider {
	return {
		provide: CONFIRMATION_SERVICE,
		useValue: {
			confirm: (message: string) => window.confirm(message)
		} satisfies ConfirmationService
	};
}
